import { FieldPath } from 'react-hook-form'
import { ConsignmentItemFormValues } from 'features/declaration/consignment-item/models/schema'
import { NctsCountry, tardekConfig } from '../../../../../../config'
import { FieldRequirement } from '../../../../models/fieldRules'
import { NctsError } from '../../../../../../shared/ncts/types'
import { isEmpty } from '../../../../../../shared/lib/isEmpty'
import getDeclarationFieldRequirementsResolver from '../../fieldRequirements/registry'
import { collectFieldPaths } from '../collectFieldPaths'
import validateDocumentFileTypes from '../documentFileTypes'
import { FieldSateValidationContext } from '../types'

const validateFieldRequirements = (
  values: ConsignmentItemFormValues,
  fields: string[],
  getFieldRequirement: (path: string) => FieldRequirement,
  addError: (path: string, message: string, code?: string) => void,
) => {
  for (const path of fields) {
    const requirement = getFieldRequirement(path)
    if (requirement === 'required') {
      const value = values[path as keyof ConsignmentItemFormValues]
      if (isEmpty(value)) {
        addError(path, 'Field is required')
      }
    }
  }
}

const validateFormFieldStates = ({
  isDraft,
  formValues,
  getFieldRequirement,
  country,
}: {
  isDraft: boolean
  formValues: ConsignmentItemFormValues
  getFieldRequirement: (path: string) => FieldRequirement
  country: string
}) => {
  const errors: NctsError[] = []

  const addError = (path: string, message: string, code?: string) => {
    errors.push({
      scope: 'consignmentItem',
      path,
      message,
      code,
    })
  }

  if (isDraft) {
    return errors
  }

  const allFields = collectFieldPaths(formValues) as FieldPath<ConsignmentItemFormValues>[]
  validateFieldRequirements(formValues, allFields, getFieldRequirement, addError)
  errors.push(...validateDocumentFileTypes(formValues, country, 'consignmentItem'))

  return errors
}

function validateConsignmentItemFormFieldStates(
  formValues: ConsignmentItemFormValues,
  ctx: FieldSateValidationContext | undefined,
): NctsError[] {
  const country = ctx?.country ?? '' as NctsCountry
  const isDraft = ctx?.isDraft ?? true
  const isPhase6OptIn = tardekConfig.phase6OptInCountries.includes(country)
  const isPhase6OptOut = tardekConfig.phase6OptOutCountries.includes(country)
  const security = ctx?.security ?? '0'
  const phase = isPhase6OptIn || isPhase6OptOut ? 'p6' : 'p5'
  const additionalDeclarationType = ctx?.additionalDeclarationType ?? 'A'
  const specificCircumstanceIndicator = ctx?.specificCircumstanceIndicator ?? ''
  const isEns = ctx?.isEns ?? false

  const resolveRequirement = getDeclarationFieldRequirementsResolver('consignmentItem', isEns)

  return validateFormFieldStates({
    isDraft,
    formValues,
    country,
    getFieldRequirement: (path: string) => resolveRequirement(path, {
      country,
      phase,
      security,
      valuesMap: formValues,
      isOptIn: isPhase6OptIn,
      additionalDeclarationType,
      specificCircumstanceIndicator,
      serverValidationResult: ctx?.serverValidationResult,
    }),
  })
}

export default validateConsignmentItemFormFieldStates
